import { useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { CalendarDays } from 'lucide-react';
import { Button } from '@/common/components/ui/button';
import { Calendar } from '@/common/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/common/components/ui/popover';

export interface CustomRangePopoverProps {
  active: boolean;
  initialFrom: Date;
  initialTo: Date;
  onApply: (range: { from: Date; to: Date }) => void;
  /**
   * Overrides the "current time" snapshot taken when the popover opens — for
   * stories/tests only. Production callers omit this; the real `Date.now()` is
   * used, so today's date and time are always correct regardless of how long
   * the page has been open.
   */
  now?: Date;
}

function toTimeInput(date: Date): string {
  // Local time, matching combineDateAndTime's setHours() below — using
  // toISOString() (UTC) here caused the popover to redisplay the wrong time in
  // any timezone with a non-zero UTC offset.
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function combineDateAndTime(day: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number);
  const combined = new Date(day);
  combined.setHours(hours, minutes, 0, 0);
  return combined;
}

/**
 * Popover housing the custom-range picker: a range-mode calendar plus two time inputs.
 * Its own draft state is separate from the applied range — nothing is committed until
 * "Apply" is pressed, so opening the popover to look around never changes the chart.
 */
export function CustomRangePopover({
  active,
  initialFrom,
  initialTo,
  onApply,
  now,
}: CustomRangePopoverProps) {
  const [open, setOpen] = useState(false);
  const [calendarRange, setCalendarRange] = useState<DateRange | undefined>();
  const [fromTime, setFromTime] = useState('00:00');
  const [toTime, setToTime] = useState('23:59');
  // Snapshotted fresh each time the popover opens, rather than trusting a
  // parent-supplied `now` that could be stale from whenever the page mounted —
  // "today" and "the current time" must reflect the moment this actually opens.
  const [nowAtOpen, setNowAtOpen] = useState(() => now ?? new Date());

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setNowAtOpen(now ?? new Date());
      setCalendarRange({ from: initialFrom, to: initialTo });
      setFromTime(toTimeInput(initialFrom));
      setToTime(toTimeInput(initialTo));
    }
    setOpen(nextOpen);
  };

  const handleApply = () => {
    if (!calendarRange?.from || !calendarRange?.to) return;

    let from = combineDateAndTime(calendarRange.from, fromTime);
    let to = combineDateAndTime(calendarRange.to, toTime);
    if (to > nowAtOpen) to = nowAtOpen;
    if (from > to) from = to;

    onApply({ from, to });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {/* size="default" (not "sm") to match the ToggleGroupItem presets' height (h-9) —
            they default to a different size scale, so this trigger was 4px shorter. */}
        <Button variant={active ? 'default' : 'outline'} className="gap-1.5">
          <CalendarDays size={14} />
          <Trans>Custom</Trans>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="range"
          selected={calendarRange}
          onSelect={setCalendarRange}
          disabled={{ after: nowAtOpen }}
          numberOfMonths={1}
        />
        <div className="space-y-3 border-t px-3 pb-3 pt-2.5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground" htmlFor="range-from-time">
                <Trans>From</Trans>
              </label>
              <input
                id="range-from-time"
                type="time"
                value={fromTime}
                onChange={(event) => setFromTime(event.target.value)}
                className="w-full rounded-md border border-input bg-muted px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground" htmlFor="range-to-time">
                <Trans>To</Trans>
              </label>
              <input
                id="range-to-time"
                type="time"
                value={toTime}
                onChange={(event) => setToTime(event.target.value)}
                className="w-full rounded-md border border-input bg-muted px-2 py-1 text-sm"
              />
            </div>
          </div>
          <Button
            onClick={handleApply}
            disabled={!calendarRange?.from || !calendarRange?.to}
            className="w-full"
            size="sm"
          >
            {t`Apply`}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
